/**
 * Warehouse library tabs (docs/warehouse.md): Transfers, Requests, Purchase orders, Subrentals.
 * The active tab lives in `?tab=`; older values keep working so stored links, notifications and
 * bookmarks still land on the right list.
 */

export type WarehouseTabKey = 'transfers' | 'requests' | 'purchase_orders' | 'subrentals';

export type WarehouseTabDefinition = {
  key: WarehouseTabKey;
  label: string;
  /** Shorter label for phones. */
  shortLabel: string;
  /** Feature that must be viewable for the tab to show (pages gate on `transfers` already). */
  featureKey: string | null;
};

export const WAREHOUSE_TABS: readonly WarehouseTabDefinition[] = [
  { key: 'transfers', label: 'Transfers', shortLabel: 'Transfers', featureKey: null },
  { key: 'requests', label: 'Requests', shortLabel: 'Requests', featureKey: null },
  { key: 'purchase_orders', label: 'Purchase orders', shortLabel: 'POs', featureKey: 'maintenance.purchase_orders' },
  { key: 'subrentals', label: 'Subrentals', shortLabel: 'Subrentals', featureKey: 'maintenance.xrental_orders' },
];

export type TransferDirection = 'both' | 'out' | 'in';

export type WarehouseTabRequest = {
  tab: WarehouseTabKey;
  /** Legacy `?tab=outbound|inbound` links pick a direction on the Transfers tab. */
  direction?: TransferDirection;
  /** Legacy `?tab=in_transit|completed` links pick a status preset on the Transfers tab. */
  statuses?: string[];
  /** True when the URL used an old value and should be rewritten to the canonical one. */
  legacy: boolean;
};

export const TRANSFER_IN_TRANSIT_PRESET = ['partially_shipped', 'shipped', 'in_transit'];
export const TRANSFER_COMPLETED_PRESET = ['received', 'complete'];

/** Reads `?tab=`; unknown or missing values fall back to Transfers. */
export function parseWarehouseTab(raw: string | null | undefined): WarehouseTabRequest {
  const value = String(raw ?? '').trim().toLowerCase();
  switch (value) {
    case 'transfers':
    case 'requests':
    case 'purchase_orders':
    case 'subrentals':
      return { tab: value, legacy: false };
    case 'xrental_orders':
    case 'vendor_rentals':
      return { tab: 'subrentals', legacy: true };
    case 'outbound':
      return { tab: 'transfers', direction: 'out', legacy: true };
    case 'inbound':
      return { tab: 'transfers', direction: 'in', legacy: true };
    case 'in_transit':
      return { tab: 'transfers', statuses: TRANSFER_IN_TRANSIT_PRESET, legacy: true };
    case 'completed':
      return { tab: 'transfers', statuses: TRANSFER_COMPLETED_PRESET, legacy: true };
    default:
      return { tab: 'transfers', legacy: value.length > 0 };
  }
}

export function warehouseTabHref(tab: WarehouseTabKey, extra?: { [key: string]: string | null | undefined }): string {
  const params = new URLSearchParams();
  params.set('tab', tab);
  Object.entries(extra ?? {}).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  return `/transfers?${params.toString()}`;
}

/** Record links (docs/warehouse.md). Records open in the same tab. */
export const warehouseRecordHref = {
  transfer: (id: string, section?: string) => `/transfers/${encodeURIComponent(id)}${section ? `/${section}` : ''}`,
  purchaseOrder: (id: string, section?: string) => `/transfers/purchase-orders/${encodeURIComponent(id)}${section ? `/${section}` : ''}`,
  subrental: (id: string, section?: string) => `/transfers/xrental-orders/${encodeURIComponent(id)}${section ? `/${section}` : ''}`,
  event: (id: string) => `/events/${encodeURIComponent(id)}`,
};
